import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Wrench, Search, Users, Clock, Calendar, Plus } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

interface Course {
  id: string;
  title: string;
  description: string;
  status: string;
  max_participants: number;
  duration_hours: number;
  created_at: string;
  enrollments?: any[];
}

export default function CourseBuilderIndex() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const createNewCourse = async () => {
    if (!user) return;

    try {
      // Create a new blank course
      const { data: newCourse, error } = await supabase
        .from('courses')
        .insert({
          title: 'Untitled Course',
          description: 'New course description',
          status: 'draft',
          created_by: user.id,
          instructor_id: user.id,
          is_online: true
        })
        .select()
        .single();

      if (error) throw error;

      // Navigate to the course builder with the new course ID
      navigate(`/course-builder/${newCourse.id}`);
    } catch (error) {
      console.error('Error creating course:', error);
      toast({
        title: "Error",
        description: "Failed to create new course",
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    fetchMyCourses();
  }, [user]);

  const fetchMyCourses = async () => {
    if (!user) return;

    try {
      console.log('Fetching courses for user:', user.id);
      
      // Get courses where user is instructor or creator
      const { data, error } = await supabase
        .from('courses')
        .select('*')
        .or(`instructor_id.eq.${user.id},created_by.eq.${user.id}`)
        .order('created_at', { ascending: false });

      console.log('Courses query result:', { data, error });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      // Get enrollment counts for each course
      if (data && data.length > 0) {
        const courseIds = data.map(course => course.id);
        console.log('Fetching enrollment counts for courses:', courseIds);
        
        const { data: enrollmentData, error: enrollmentError } = await supabase
          .from('enrollments')
          .select('course_id')
          .in('course_id', courseIds)
          .in('status', ['enrolled', 'completed']);

        if (enrollmentError) {
          console.error('Enrollment query error:', enrollmentError);
        } else {
          console.log('Enrollment data:', enrollmentData);
        }

        // Count enrollments per course
        const enrollmentCounts: Record<string, number> = {};
        enrollmentData?.forEach(enrollment => {
          enrollmentCounts[enrollment.course_id] = (enrollmentCounts[enrollment.course_id] || 0) + 1;
        });

        // Add enrollment counts to courses
        const coursesWithCounts = data.map(course => ({
          ...course,
          enrollments: [{ count: enrollmentCounts[course.id] || 0 }]
        }));

        console.log('Final courses with counts:', coursesWithCounts);
        setCourses(coursesWithCounts);
      } else {
        console.log('No courses found for user');
        setCourses([]);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast({
        title: "Error",  
        description: `Failed to fetch your courses: ${error.message}`,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredCourses = courses.filter(course =>
    course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    course.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="p-6">Loading your courses...</div>;
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Wrench className="h-8 w-8 text-primary" />
            Course Builder
          </h1>
          <p className="text-muted-foreground mt-1">
            Create and edit comprehensive learning experiences
          </p>
        </div>
        <Button onClick={createNewCourse}>
          <Plus className="h-4 w-4 mr-2" />
          Create New Course
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
        <Input
          placeholder="Search courses..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Course Grid */}
      {filteredCourses.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Wrench className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No courses found</h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm 
                ? "No courses match your search criteria" 
                : "You haven't created any courses yet"}
            </p>
            {!searchTerm && (
              <Button onClick={createNewCourse}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Course
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredCourses.map((course) => {
            const enrollmentCount = course.enrollments?.[0]?.count || 0;
            
            return (
              <Card key={course.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <Badge variant={course.status === 'published' ? 'default' : 'secondary'}>
                      {course.status}
                    </Badge>
                    <Button
                      size="sm"
                      onClick={() => navigate(`/course-builder/${course.id}`)}
                    >
                      <Wrench className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                  </div>
                  <CardTitle className="text-lg">{course.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
                    {course.description}
                  </p>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Users className="h-4 w-4" />
                      <span>{enrollmentCount} enrolled</span>
                      {course.max_participants && (
                        <span>/ {course.max_participants} max</span>
                      )}
                    </div>
                    
                    {course.duration_hours && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        <span>{course.duration_hours} hours</span>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      <span>Created {new Date(course.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t">
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => navigate(`/course-management/${course.id}`)}
                    >
                      View Details
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Course Builder Features */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Course Builder Features</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div className="space-y-2">
              <h4 className="font-medium text-primary">Delivery Methods</h4>
              <ul className="space-y-1 text-muted-foreground">
                <li>• In-Person Classes</li>
                <li>• Hybrid Learning</li>
                <li>• Distance Synchronous</li>
                <li>• Self-Paced Async</li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium text-primary">Content Types</h4>
              <ul className="space-y-1 text-muted-foreground">
                <li>• Video & Audio Files</li>
                <li>• Documents & PDFs</li>
                <li>• PowerPoint & Excel</li>
                <li>• SCORM Packages</li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium text-primary">Media Support</h4>
              <ul className="space-y-1 text-muted-foreground">
                <li>• YouTube Integration</li>
                <li>• Vimeo Integration</li>
                <li>• Image Galleries</li>
                <li>• Audio Recordings</li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium text-primary">Advanced Features</h4>
              <ul className="space-y-1 text-muted-foreground">
                <li>• Modular Organization</li>
                <li>• Progress Tracking</li>
                <li>• Custom Completion</li>
                <li>• File Management</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}